import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { RuleMetadataProvider } from './metadata/RuleMetadataProvider';
import RuleEditor from './editor/RuleEditor';
import RuleList from './RuleList';

interface ComponentProps {
  /** Undefined means the logged-in user's own company; the admin section passes the row it opened. */
  companyId?: string;
  readOnly?: boolean;
}

/** `?rule=<id>` opens that rule, `?rule=new` starts one, absent shows the list. */
const RULE_PARAM = 'rule';
const NEW_RULE = 'new';

/**
 * The company-agnostic rule screen, shared by the company settings page and the admin section.
 *
 * Which rule is open lives in the URL rather than in component state, so a rule can be linked to,
 * and the browser's back button steps out of the editor the way a reader expects.
 */
const AiDispatcherRules: React.FC<ComponentProps> = ({ companyId, readOnly }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const openRule = searchParams.get(RULE_PARAM);

  const showRule = (ruleId?: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(RULE_PARAM, ruleId ?? NEW_RULE);
    setSearchParams(next);
  };

  const showList = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(RULE_PARAM);
    setSearchParams(next);
  };

  return (
    <RuleMetadataProvider>
      {openRule ? (
        <RuleEditor
          companyId={companyId}
          ruleId={openRule === NEW_RULE ? undefined : openRule}
          readOnly={readOnly}
          onClose={showList}
          // A new rule becomes an existing one on its first save, so the URL has to catch up —
          // otherwise a second save would create a second rule.
          onSaved={ruleId => showRule(ruleId)}
        />
      ) : (
        <RuleList companyId={companyId} readOnly={readOnly} onOpenRule={showRule} />
      )}
    </RuleMetadataProvider>
  );
};

export default AiDispatcherRules;
